"use client";

import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { BackLink } from "@/components/shared/back-link";
import { ArrowRightIcon, Emphasis, STEP_CTA_CLASS, StepHeader } from "@/components/shared/step-header";
import { FieldHelp } from "@/components/shared/field";
import { maskCpf, isValidCpf } from "@/lib/cpf";
import type { OnboardingVoice } from "@/lib/onboarding/onboarding-voice";
import { onlyDigits } from "@/lib/strings";

/** The voice's optional words, when the door does not bring its own. */
const DEFAULT_FIELD_LABEL = "CPF";
const DEFAULT_SUBMIT_LABEL = "Continuar";
const DEFAULT_EXIT_LABEL = "voltar para a Homepage";
const DEFAULT_INCOMPLETE_MESSAGE = "Informe os onze dígitos do seu CPF.";

// RHF-compatible schema — validates clean digits stored in the hidden field.
function buildCpfFormSchema(incompleteMessage: string) {
  return z.object({
    cpf: z
      .string()
      .min(11, incompleteMessage)
      .max(11, incompleteMessage)
      .refine(isValidCpf, "Esse CPF não confere. Confira os números e tente de novo."),
  });
}

type CpfFormValues = z.infer<ReturnType<typeof buildCpfFormSchema>>;

interface CpfStepFormProps {
  isPending: boolean;
  errorMessage: string | null;
  /** What this door says — see `ONBOARDING_VOICE`. */
  voice: OnboardingVoice["cpf"];
  onSubmit: (cpf: string) => void;
  /** The way out, above the eyebrow (`voice.exitLabel`, "voltar para a Homepage" by default). */
  onExit?: () => void;
  /**
   * Said when more than eleven digits arrive — a CNPJ. Only the host door has words for it: the
   * prototype's sentence speaks of the list.
   */
  cnpjMessage?: string;
}

export function CpfStepForm({ isPending, errorMessage, voice, onSubmit, onExit, cnpjMessage }: CpfStepFormProps) {
  const [masked, setMasked] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  // The step opens on its one field, as the prototype does.
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const {
    handleSubmit,
    setValue,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm<CpfFormValues>({
    defaultValues: { cpf: "" },
  });

  function handleChange(event: React.ChangeEvent<HTMLInputElement>) {
    const typed = onlyDigits(event.target.value);
    const digits = typed.slice(0, 11);
    setValue("cpf", digits);
    setMasked(maskCpf(digits));
    if (typed.length > 11 && cnpjMessage) {
      setError("cpf", { message: cnpjMessage });
      return;
    }
    if (errors.cpf) clearErrors("cpf");
  }

  function onFormSubmit(data: CpfFormValues) {
    // Manual validation with Zod — zodResolver + .refine() has integration
    // issues with Zod 4; explicit safeParse gives us full control.
    const result = buildCpfFormSchema(voice.incompleteMessage ?? DEFAULT_INCOMPLETE_MESSAGE).safeParse(data);
    if (!result.success) {
      setError("cpf", { message: result.error.issues[0].message });
      return;
    }
    onSubmit(result.data.cpf);
  }

  const fieldError = errors.cpf?.message;
  const helpText = fieldError ?? errorMessage;

  return (
    <form data-clarity-mask="true" onSubmit={handleSubmit(onFormSubmit)} className="flex w-full flex-col">
      {onExit && <BackLink onClick={onExit}>{voice.exitLabel ?? DEFAULT_EXIT_LABEL}</BackLink>}
      <StepHeader
        eyebrow={voice.eyebrow}
        title={
          <>
            {voice.titleLead} <Emphasis>CPF</Emphasis>
            {voice.titleTail ? ` ${voice.titleTail}` : null}
          </>
        }
        lede={voice.lede}
      />

      <div className="flex flex-col gap-2">
        <Label htmlFor="cpf">{voice.fieldLabel ?? DEFAULT_FIELD_LABEL}</Label>
        <Input
          id="cpf"
          ref={inputRef}
          inputMode="numeric"
          autoComplete="off"
          placeholder="000.000.000-00"
          value={masked}
          aria-invalid={!!fieldError || !!errorMessage}
          aria-describedby="cpf-help"
          onChange={handleChange}
        />
        <FieldHelp error={!!helpText} id="cpf-help">
          {helpText ?? voice.help}
        </FieldHelp>
      </div>

      <Button type="submit" size="lg" disabled={isPending} className={`mt-7 w-full gap-2.5 ${STEP_CTA_CLASS}`}>
        {isPending ? "Verificando..." : (voice.submitLabel ?? DEFAULT_SUBMIT_LABEL)}
        <ArrowRightIcon />
      </Button>
    </form>
  );
}
